import { fmtInt, t } from '../i18n/i18n';
import { useMemo, useState } from 'react';
import { allowedStitchTypes, createThread, stitchTypeName } from '../engine/model/design';
import { objectBBox } from '../engine/model/geometry';
import { LIMITS } from '../engine/model/params';
import { MACHINES, hoopForMachine, hoopsFor } from '../engine/model/machines';
import type { Design, EmbObject, FillParams, MachineSettings, RunningParams, SatinParams, StitchType } from '../engine/model/types';
import { formatDuration } from '../engine/stitch/stats';
import {
  addColor,
  colorUsage,
  groupByColor,
  mergeColors,
  moveObject,
  removeUnusedColors,
  scaleDesign,
  setStitchType,
  updateColor,
  updateObjects,
} from '../state/designOps';
import { setPreferredMachine } from '../state/machinePref';
import { store, useApp } from '../state/store';
import { deleteSelected, duplicateSelected, select } from './actions';
import { IconCopy, IconDown, IconPlus, IconTrash, IconUp } from './icons';
import { IssueList } from './Issues';
import { NumberField } from './NumberField';
import { SheetClose } from './PhoneLayout';
import { usePhone } from './usePhone';

type Tab = 'object' | 'colors' | 'machine';

export function Inspector() {
  const [tab, setTab] = useState<Tab>('object');
  const phone = usePhone();
  return (
    <aside className="inspector" aria-label={t('Properties')}>
      <div className="tabs" role="tablist">
        {(
          [
            ['object', t('Object')],
            ['colors', t('Colours')],
            ['machine', t('Machine')],
          ] as const
        ).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
        {phone && <SheetClose />}
      </div>
      <div className="panel-body">
        {tab === 'object' && <ObjectTab />}
        {tab === 'colors' && <ColorsTab />}
        {tab === 'machine' && <MachineTab />}
      </div>
    </aside>
  );
}

const commit = (d: Design, key: string | null = null) => store.commitDesign(d, key);
const patchObj = (id: string, f: (o: EmbObject) => EmbObject, key: string | null = null) => commit(updateObjects(store.design, [id], f), key);

function ObjectTab() {
  const design = useApp((s) => s.history.present);
  const selection = useApp((s) => s.selection);
  const result = useApp((s) => s.stitch.result);
  const selected = design.objects.filter((o) => selection.includes(o.id));
  if (!selected.length) {
    return (
      <div className="empty">
        <strong>{t('No objects selected')}</strong>
        {t('Select an object on the canvas or in the object list to change its shape, colour and stitches. Draw new shapes with the tools on the left.')}
      </div>
    );
  }
  if (selected.length > 1) return <MultiObject objs={selected} design={design} />;
  const o = selected[0];
  const index = design.objects.indexOf(o);
  const issues = result?.issues.filter((i) => i.objectId === o.id) ?? [];
  return (
    <>
      <div className="section">
        <div className="row">
          <input
            className="input"
            aria-label={t('Object name')}
            value={o.name}
            onChange={(e) => patchObj(o.id, (x) => ({ ...x, name: e.target.value }), `name:${o.id}`)}
          />
        </div>
        <div className="row" style={{ marginTop: 8 }}>
          <ColorSelect design={design} value={o.colorId} onChange={(c) => patchObj(o.id, (x) => ({ ...x, colorId: c }))} />
        </div>
        <p className="hint">
          {t('{kind} · no. {n} of {total} in the sewing order · {stitches} stitches', {
            kind: o.shape.kind === 'area' ? t('Area') : o.shape.kind === 'line' ? (o.shape.closed ? t('Closed line') : t('Line')) : t('Satin column'),
            n: index + 1,
            total: design.objects.length,
            stitches: result?.objectStitchCounts[o.id] ?? '…',
          })}
          {o.locked ? ' · ' + t('locked') : ''}
        </p>
      </div>
      {issues.length > 0 && (
        <div className="section">
          <IssueList issues={issues} />
        </div>
      )}
      <StitchSection o={o} />
      <GeometrySection o={o} />
      <div className="section">
        <h3>{t('Start, end and tie-off')}</h3>
        <div className="row wrap">
          <button className="btn small" onClick={() => store.set({ pick: 'entry' })} disabled={o.locked}>
            {o.entry ? t('Move start point') : t('Choose start point')}
          </button>
          {o.entry && (
            <button className="btn small ghost" onClick={() => patchObj(o.id, (x) => ({ ...x, entry: null }))}>
              {t('Automatic')}
            </button>
          )}
        </div>
        <div className="row wrap" style={{ marginTop: 6 }}>
          <button className="btn small" onClick={() => store.set({ pick: 'exit' })} disabled={o.locked}>
            {o.exit ? t('Move end point') : t('Choose end point')}
          </button>
          {o.exit && (
            <button className="btn small ghost" onClick={() => patchObj(o.id, (x) => ({ ...x, exit: null }))}>
              {t('Automatic')}
            </button>
          )}
        </div>
        <p className="hint">{t('Running stitch and satin run towards the end point. Fill starts and ends at the nearest possible row ends (where the rows are shortest).')}</p>
        <label className="check">
          <input type="checkbox" checked={o.tieIn} onChange={(e) => patchObj(o.id, (x) => ({ ...x, tieIn: e.target.checked }))} /> {t('Tie-in at start')}
        </label>
        <label className="check">
          <input type="checkbox" checked={o.tieOff} onChange={(e) => patchObj(o.id, (x) => ({ ...x, tieOff: e.target.checked }))} /> {t('Tie-off before trims')}
        </label>
      </div>
      <div className="section">
        <h3>{t('Order')}</h3>
        <div className="row wrap">
          <button className="btn small" onClick={() => commit(moveObject(design, o.id, 0))} disabled={index === 0}>
            {t('First')}
          </button>
          <button className="btn small icon" aria-label={t('Earlier')} onClick={() => commit(moveObject(design, o.id, index - 1))} disabled={index === 0}>
            <IconUp />
          </button>
          <button className="btn small icon" aria-label={t('Later')} onClick={() => commit(moveObject(design, o.id, index + 1))} disabled={index === design.objects.length - 1}>
            <IconDown />
          </button>
          <button className="btn small" onClick={() => commit(moveObject(design, o.id, design.objects.length - 1))} disabled={index === design.objects.length - 1}>
            {t('Last')}
          </button>
        </div>
        <div className="row" style={{ marginTop: 8 }}>
          <button className="btn small" onClick={() => duplicateSelected()}>
            <IconCopy size={14} /> {t('Duplicate')}
          </button>
          <button className="btn small danger" onClick={() => deleteSelected()} disabled={o.locked}>
            <IconTrash size={14} /> {t('Delete')}
          </button>
        </div>
      </div>
    </>
  );
}

function MultiObject({ objs, design }: { objs: EmbObject[]; design: Design }) {
  const ids = objs.map((o) => o.id);
  const types = new Set(objs.map((o) => o.params.type));
  return (
    <div className="section">
      <h3>{t('{n} objects selected', { n: objs.length })}</h3>
      <div className="field">
        <span>{t('Colour for all')}</span>
        <ColorSelect design={design} value={objs.every((o) => o.colorId === objs[0].colorId) ? objs[0].colorId : ''} onChange={(c) => commit(updateObjects(design, ids, (o) => ({ ...o, colorId: c })))} />
      </div>
      <div className="field" style={{ marginTop: 8 }}>
        <span>{t('Stitch type for all')}</span>
        <select
          className="input"
          aria-label={t('Stitch type for all')}
          value={types.size === 1 ? [...types][0] : ''}
          onChange={(e) => commit(updateObjects(design, ids, (o) => (allowedStitchTypes(o.shape.kind, o.shape.kind !== 'line' || o.shape.closed).includes(e.target.value as StitchType) ? setStitchType(o, e.target.value as StitchType) : o)))}
        >
          {types.size !== 1 && <option value="">{t('Mixed')}</option>}
          {(['fill', 'satin', 'running'] as const).map((st) => (
            <option key={st} value={st}>
              {stitchTypeName(st)}
            </option>
          ))}
        </select>
      </div>
      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn small" onClick={() => duplicateSelected()}>
          <IconCopy size={14} /> {t('Duplicate')}
        </button>
        <button className="btn small danger" onClick={() => deleteSelected()}>
          <IconTrash size={14} /> {t('Delete')}
        </button>
      </div>
    </div>
  );
}

function ColorSelect({ design, value, onChange }: { design: Design; value: string; onChange: (id: string) => void }) {
  const hex = design.palette.find((c) => c.id === value)?.hex ?? '#000000';
  return (
    <div className="row" style={{ width: '100%' }}>
      <span className="swatch lg" style={{ background: value ? hex : 'transparent' }} />
      <select className="input" aria-label={t('Thread colour')} value={value} onChange={(e) => onChange(e.target.value)}>
        {!value && <option value="">{t('Mixed')}</option>}
        {design.palette.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name} ({c.hex})
          </option>
        ))}
      </select>
    </div>
  );
}

function StitchSection({ o }: { o: EmbObject }) {
  const key = (f: string) => `param:${o.id}:${f}`;
  const setP = (p: EmbObject['params'], f: string) => patchObj(o.id, (x) => ({ ...x, params: p }), key(f));
  const allowed = allowedStitchTypes(o.shape.kind, o.shape.kind !== 'line' || o.shape.closed);
  return (
    <div className="section">
      <h3>{t('Stitches')}</h3>
      <div className="field">
        <span>{t('Stitch type')}</span>
        <select className="input" aria-label={t('Stitch type')} value={o.params.type} disabled={o.locked} onChange={(e) => patchObj(o.id, (x) => setStitchType(x, e.target.value as StitchType))}>
          {allowed.map((st) => (
            <option key={st} value={st}>
              {stitchTypeName(st)}
            </option>
          ))}
        </select>
      </div>
      <div style={{ marginTop: 8 }}>
        {o.params.type === 'running' && <RunningFields p={o.params} set={setP} disabled={o.locked} />}
        {o.params.type === 'fill' && <FillFields p={o.params} set={setP} disabled={o.locked} />}
        {o.params.type === 'satin' && <SatinFields p={o.params} set={setP} disabled={o.locked} isLine={o.shape.kind === 'line'} />}
      </div>
    </div>
  );
}

type SetP = (p: EmbObject['params'], field: string) => void;

function RunningFields({ p, set, disabled }: { p: RunningParams; set: SetP; disabled: boolean }) {
  const L = LIMITS.running;
  return (
    <div className="grid2">
      <NumberField label={t(L.stitchLength.label)} limit={L.stitchLength} value={p.stitchLength} disabled={disabled} onChange={(v) => set({ ...p, stitchLength: v }, 'len')} />
      <NumberField label={t(L.repeats.label)} limit={L.repeats} decimals={0} value={p.repeats} disabled={disabled} onChange={(v) => set({ ...p, repeats: Math.round(v) }, 'rep')} title={t('1 = single, 3 = triple running stitch')} />
    </div>
  );
}

function FillFields({ p, set, disabled }: { p: FillParams; set: SetP; disabled: boolean }) {
  const L = LIMITS.fill;
  const U = LIMITS.fillUnderlay;
  const u = p.underlay;
  return (
    <>
      <div className="grid2">
        <NumberField label={t(L.angle.label)} limit={L.angle} decimals={0} value={p.angle} disabled={disabled} onChange={(v) => set({ ...p, angle: v }, 'angle')} />
        <NumberField label={t(L.spacing.label)} limit={L.spacing} value={p.spacing} disabled={disabled} onChange={(v) => set({ ...p, spacing: v }, 'spacing')} />
        <NumberField label={t(L.stitchLength.label)} limit={L.stitchLength} value={p.stitchLength} disabled={disabled} onChange={(v) => set({ ...p, stitchLength: v }, 'len')} />
        <NumberField label={t(L.minStitchLength.label)} limit={L.minStitchLength} value={p.minStitchLength} disabled={disabled} onChange={(v) => set({ ...p, minStitchLength: v }, 'minlen')} />
        <NumberField label={t(L.stagger.label)} limit={L.stagger} decimals={0} value={p.stagger} disabled={disabled} onChange={(v) => set({ ...p, stagger: Math.round(v) }, 'stagger')} />
        <NumberField label={t(L.pullCompensation.label)} limit={L.pullCompensation} value={p.pullCompensation} disabled={disabled} onChange={(v) => set({ ...p, pullCompensation: v }, 'pull')} />
        <NumberField label={t(L.overlap.label)} limit={L.overlap} value={p.overlap} disabled={disabled} onChange={(v) => set({ ...p, overlap: v }, 'overlap')} title={t('Grows the shape so it overlaps neighbouring objects')} />
      </div>
      <h3 style={{ marginTop: 12 }}>{t('Underlay')}</h3>
      <label className="check">
        <input type="checkbox" checked={u.edgeRun} disabled={disabled} onChange={(e) => set({ ...p, underlay: { ...u, edgeRun: e.target.checked } }, 'u-edge')} /> {t('Edge run')}
      </label>
      <label className="check">
        <input type="checkbox" checked={u.tatami} disabled={disabled} onChange={(e) => set({ ...p, underlay: { ...u, tatami: e.target.checked } }, 'u-tat')} /> {t('Tatami underlay (90°)')}
      </label>
      {(u.edgeRun || u.tatami) && (
        <div className="grid2" style={{ marginTop: 6 }}>
          <NumberField label={t(U.inset.label)} limit={U.inset} value={u.inset} disabled={disabled} onChange={(v) => set({ ...p, underlay: { ...u, inset: v } }, 'u-inset')} />
          <NumberField label={t(U.spacing.label)} limit={U.spacing} value={u.spacing} disabled={disabled} onChange={(v) => set({ ...p, underlay: { ...u, spacing: v } }, 'u-sp')} />
          <NumberField label={t(U.stitchLength.label)} limit={U.stitchLength} value={u.stitchLength} disabled={disabled} onChange={(v) => set({ ...p, underlay: { ...u, stitchLength: v } }, 'u-len')} />
        </div>
      )}
    </>
  );
}

function SatinFields({ p, set, disabled, isLine }: { p: SatinParams; set: SetP; disabled: boolean; isLine: boolean }) {
  const L = LIMITS.satin;
  const U = LIMITS.satinUnderlay;
  const u = p.underlay;
  return (
    <>
      <div className="grid2">
        <NumberField label={t(L.spacing.label)} limit={L.spacing} value={p.spacing} disabled={disabled} onChange={(v) => set({ ...p, spacing: v }, 'spacing')} />
        <NumberField label={t(L.pullCompensation.label)} limit={L.pullCompensation} value={p.pullCompensation} disabled={disabled} onChange={(v) => set({ ...p, pullCompensation: v }, 'pull')} />
        {isLine && <NumberField label={t(L.lineWidth.label)} limit={L.lineWidth} value={p.lineWidth} disabled={disabled} onChange={(v) => set({ ...p, lineWidth: v }, 'width')} />}
        <NumberField label={t(L.maxWidth.label)} limit={L.maxWidth} value={p.maxWidth} disabled={disabled} onChange={(v) => set({ ...p, maxWidth: v }, 'maxw')} />
      </div>
      <label className="check">
        <input type="checkbox" checked={p.splitWide} disabled={disabled} onChange={(e) => set({ ...p, splitWide: e.target.checked }, 'split')} /> {t('Split wide stitches')}
      </label>
      <label className="check">
        <input type="checkbox" checked={p.shortStitches} disabled={disabled} onChange={(e) => set({ ...p, shortStitches: e.target.checked }, 'short')} /> {t('Short stitches in tight curves')}
      </label>
      <h3 style={{ marginTop: 12 }}>{t('Underlay')}</h3>
      <label className="check">
        <input type="checkbox" checked={u.centerRun} disabled={disabled} onChange={(e) => set({ ...p, underlay: { ...u, centerRun: e.target.checked } }, 'u-c')} /> {t('Centre run')}
      </label>
      <label className="check">
        <input type="checkbox" checked={u.edgeRun} disabled={disabled} onChange={(e) => set({ ...p, underlay: { ...u, edgeRun: e.target.checked } }, 'u-e')} /> {t('Edge run')}
      </label>
      <label className="check">
        <input type="checkbox" checked={u.zigzag} disabled={disabled} onChange={(e) => set({ ...p, underlay: { ...u, zigzag: e.target.checked } }, 'u-z')} /> {t('Zigzag underlay')}
      </label>
      <div className="grid2" style={{ marginTop: 6 }}>
        <NumberField label={t(U.inset.label)} limit={U.inset} value={u.inset} disabled={disabled} onChange={(v) => set({ ...p, underlay: { ...u, inset: v } }, 'u-in')} />
        <NumberField label={t(U.zigzagSpacing.label)} limit={U.zigzagSpacing} value={u.zigzagSpacing} disabled={disabled} onChange={(v) => set({ ...p, underlay: { ...u, zigzagSpacing: v } }, 'u-zs')} />
        <NumberField label={t(U.stitchLength.label)} limit={U.stitchLength} value={u.stitchLength} disabled={disabled} onChange={(v) => set({ ...p, underlay: { ...u, stitchLength: v } }, 'u-len')} />
      </div>
    </>
  );
}

const SIZE_LIMIT = { min: 0.5, max: 2000, step: 0.5, unit: 'mm' };
const POS_LIMIT = { min: -5000, max: 5000, step: 0.5, unit: 'mm' };
const ROT_LIMIT = { min: -360, max: 360, step: 1, unit: '°' };

function GeometrySection({ o }: { o: EmbObject }) {
  const [lock, setLock] = useState(true);
  const b = objectBBox(o);
  const w = b.maxX - b.minX, h = b.maxY - b.minY;
  const k = `geom:${o.id}`;
  const rotated = Math.abs(o.transform.rotation % 360) > 1e-6;
  const scaleTo = (sx: number, sy: number) =>
    patchObj(
      o.id,
      (x) => {
        // scale around the bbox min corner
        const ax = b.minX, ay = b.minY;
        return {
          ...x,
          transform: { ...x.transform, x: ax + (x.transform.x - ax) * sx, y: ay + (x.transform.y - ay) * sy, scaleX: x.transform.scaleX * sx, scaleY: x.transform.scaleY * sy },
        };
      },
      k,
    );
  return (
    <div className="section">
      <h3>{t('Position and size')}</h3>
      <div className="grid2">
        <NumberField label="X" limit={POS_LIMIT} value={b.minX} disabled={o.locked} onChange={(v) => patchObj(o.id, (x) => ({ ...x, transform: { ...x.transform, x: x.transform.x + v - b.minX } }), k)} />
        <NumberField label="Y" limit={POS_LIMIT} value={b.minY} disabled={o.locked} onChange={(v) => patchObj(o.id, (x) => ({ ...x, transform: { ...x.transform, y: x.transform.y + v - b.minY } }), k)} />
        <NumberField
          label={t('Width')}
          limit={SIZE_LIMIT}
          value={w}
          disabled={o.locked || w < 1e-6}
          onChange={(v) => {
            const s = v / w;
            scaleTo(s, lock || rotated ? s : 1);
          }}
        />
        <NumberField
          label={t('Height')}
          limit={SIZE_LIMIT}
          value={h}
          disabled={o.locked || h < 1e-6}
          onChange={(v) => {
            const s = v / h;
            scaleTo(lock || rotated ? s : 1, s);
          }}
        />
        <NumberField
          label={t('Rotation')}
          limit={ROT_LIMIT}
          decimals={1}
          value={o.transform.rotation}
          disabled={o.locked}
          onChange={(v) =>
            patchObj(
              o.id,
              (x) => {
                // rotate around the bbox centre
                const c = { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
                const d = ((v - x.transform.rotation) * Math.PI) / 180;
                const cos = Math.cos(d), sin = Math.sin(d);
                const tx = x.transform.x - c.x, ty = x.transform.y - c.y;
                return { ...x, transform: { ...x.transform, rotation: v, x: c.x + tx * cos - ty * sin, y: c.y + tx * sin + ty * cos } };
              },
              k,
            )
          }
        />
        <label className="check" style={{ alignSelf: 'end' }}>
          <input type="checkbox" checked={lock || rotated} disabled={rotated} onChange={(e) => setLock(e.target.checked)} /> {t('Lock aspect ratio')}
        </label>
      </div>
      <label className="check" style={{ marginTop: 6 }}>
        <input type="checkbox" checked={o.locked} onChange={(e) => patchObj(o.id, (x) => ({ ...x, locked: e.target.checked }))} /> {t('Lock object')}
      </label>
    </div>
  );
}

function ColorsTab() {
  const design = useApp((s) => s.history.present);
  const usage = useMemo(() => colorUsage(design), [design]);
  const [mergeFrom, setMergeFrom] = useState<string | null>(null);
  return (
    <>
      <div className="section">
        <h3>{t('Thread palette')}</h3>
        {!design.palette.length && <p className="hint">{t('The palette is empty. Digitize an image or add a colour.')}</p>}
        <ul className="palette-list">
          {design.palette.map((c, i) => (
            <li key={c.id} className="palette-item" style={{ gridTemplateColumns: '26px 1fr auto' }}>
              <input
                type="color"
                className="swatch-input"
                aria-label={t('Colour for {name}', { name: c.name })}
                value={c.hex}
                onChange={(e) => commit(updateColor(design, c.id, { hex: e.target.value }), `color:${c.id}`)}
              />
              <div style={{ minWidth: 0 }}>
                <input className="input" style={{ height: 24 }} aria-label={t('Colour name')} value={c.name} onChange={(e) => commit(updateColor(design, c.id, { name: e.target.value }), `cname:${c.id}`)} />
                <small style={{ color: 'var(--muted)' }}>
                  {t('{n} objects', { n: usage.get(c.id) ?? 0 })} · {c.hex}
                </small>
              </div>
              <div className="row">
                <button className="btn small icon" title={t('Move up')} aria-label={t('Move colour up')} disabled={i === 0} onClick={() => commit({ ...design, palette: swap(design.palette, i, i - 1) })}>
                  <IconUp size={12} />
                </button>
                <button className="btn small" title={t('Select objects with this colour')} onClick={() => select(design.objects.filter((o) => o.colorId === c.id).map((o) => o.id))}>
                  {t('Select')}
                </button>
                <button className="btn small" title={t('Merge with another colour')} onClick={() => setMergeFrom(mergeFrom === c.id ? null : c.id)} aria-pressed={mergeFrom === c.id}>
                  {t('Merge')}
                </button>
              </div>
              {mergeFrom === c.id && (
                <div style={{ gridColumn: '1 / -1' }} className="row">
                  <span className="hint" style={{ margin: 0 }}>
                    {t('Move all objects to:')}
                  </span>
                  <select
                    className="input"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) commit(mergeColors(design, c.id, e.target.value));
                      setMergeFrom(null);
                    }}
                  >
                    <option value="">{t('Choose colour…')}</option>
                    {design.palette
                      .filter((x) => x.id !== c.id)
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </li>
          ))}
        </ul>
        <div className="row wrap" style={{ marginTop: 8 }}>
          <button className="btn small" onClick={() => commit(addColor(design, createThread('#3366cc', t('Colour {n}', { n: design.palette.length + 1 }))))}>
            <IconPlus size={14} /> {t('New colour')}
          </button>
          <button className="btn small" onClick={() => commit(removeUnusedColors(design))} disabled={design.palette.every((c) => usage.has(c.id))}>
            {t('Remove unused')}
          </button>
        </div>
      </div>
      <div className="section">
        <h3>{t('Colour changes')}</h3>
        <p className="hint">{t('Objects are sewn in list order. Grouping by colour (in palette order) gives the fewest colour changes, but may change which objects end up on top.')}</p>
        <button className="btn small" onClick={() => commit(groupByColor(design))}>
          {t('Group objects by colour')}
        </button>
      </div>
    </>
  );
}

function swap<T>(arr: T[], i: number, j: number): T[] {
  const a = arr.slice();
  [a[i], a[j]] = [a[j], a[i]];
  return a;
}

/** Turns the hoop layer on and zooms so the whole hoop is visible. */
const showHoop = () => store.set((s) => ({ hoopFitNonce: s.hoopFitNonce + 1, layers: { ...s.layers, hoop: true } }));

function MachineTab() {
  const design = useApp((s) => s.history.present);
  const result = useApp((s) => s.stitch.result);
  const m = design.machine;
  const setM = (patch: Partial<MachineSettings>, key: string | null = null) => commit({ ...design, machine: { ...m, ...patch } }, key);
  const L = LIMITS.machine;
  const stats = result?.stats;
  const [lockRatio, setLockRatio] = useState(true);
  const hoops = hoopsFor(m.machineId);
  const custom = !hoops.some((h) => h.id === m.hoop.id);
  const brands = [...new Set(MACHINES.map((x) => x.brand))];
  const w = stats?.widthMm ?? 0, h = stats?.heightMm ?? 0;
  return (
    <>
      <div className="section">
        <h3>{t('Design size')}</h3>
        <div className="grid2">
          <NumberField
            label={t('Width (stitches)')}
            limit={SIZE_LIMIT}
            value={w}
            disabled={!(w > 0)}
            onChange={(v) => {
              const s = v / w;
              commit(lockRatio ? scaleDesign(design, s) : scaleAxis(design, s, 1), 'design-size');
            }}
          />
          <NumberField
            label={t('Height (stitches)')}
            limit={SIZE_LIMIT}
            value={h}
            disabled={!(h > 0)}
            onChange={(v) => {
              const s = v / h;
              commit(lockRatio ? scaleDesign(design, s) : scaleAxis(design, 1, s), 'design-size');
            }}
          />
        </div>
        <label className="check">
          <input type="checkbox" checked={lockRatio} onChange={(e) => setLockRatio(e.target.checked)} /> {t('Keep aspect ratio')}
        </label>
        <p className="hint">{t('Scaling changes the shapes; the stitch density is kept, so the stitch count changes.')}</p>
      </div>
      <div className="section">
        <h3>{t('Hoop and placement')}</h3>
        <div className="field">
          <span>{t('Embroidery machine')}</span>
          <select
            className="input"
            aria-label={t('Embroidery machine')}
            value={m.machineId ?? ''}
            onChange={(e) => {
              const machineId = e.target.value || null;
              setPreferredMachine(machineId);
              setM({ machineId, hoop: hoopForMachine(machineId, m.hoop) });
              showHoop();
            }}
          >
            <option value="">{t('Generic (all hoop sizes)')}</option>
            {brands.map((b) => (
              <optgroup key={b} label={b}>
                {MACHINES.filter((x) => x.brand === b).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.brand} {x.model}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="field" style={{ marginTop: 6 }}>
          <span>{t('Hoop (sewing field)')}</span>
          <select
            className="input"
            aria-label={t('Hoop')}
            value={custom ? 'custom' : m.hoop.id}
            onChange={(e) => {
              const hoop = hoops.find((x) => x.id === e.target.value);
              setM({ hoop: hoop ?? { id: 'custom', name: t('Custom'), widthMm: m.hoop.widthMm, heightMm: m.hoop.heightMm } });
              showHoop();
            }}
          >
            {hoops.map((x) => (
              <option key={x.id} value={x.id}>
                {t(x.name)}
              </option>
            ))}
            <option value="custom">{t('Custom…')}</option>
          </select>
        </div>
        {m.machineId && <p className="hint">{t('Only the hoops this machine accepts are shown. Check the sizes against the manual for your machine.')}</p>}
        {custom && (
          <div className="grid2" style={{ marginTop: 6 }}>
            <NumberField label={t('Hoop width')} limit={{ min: 20, max: 1500, step: 1, unit: 'mm' }} value={m.hoop.widthMm} onChange={(v) => setM({ hoop: { ...m.hoop, widthMm: v, name: `${v} × ${m.hoop.heightMm} mm` } }, 'hoop')} />
            <NumberField label={t('Hoop height')} limit={{ min: 20, max: 1500, step: 1, unit: 'mm' }} value={m.hoop.heightMm} onChange={(v) => setM({ hoop: { ...m.hoop, heightMm: v, name: `${m.hoop.widthMm} × ${v} mm` } }, 'hoop')} />
          </div>
        )}
        <div className="row wrap" style={{ marginTop: 8 }}>
          <label className="check">
            <input type="radio" name="placement" checked={!m.hoopCenter} onChange={() => setM({ hoopCenter: null })} /> {t('Centre the design in the hoop')}
          </label>
          <label className="check">
            <input type="radio" name="placement" checked={!!m.hoopCenter} onChange={() => store.set({ pick: 'hoopCenter' })} /> {t('Custom centre point')}
          </label>
        </div>
        {m.hoopCenter && (
          <div className="grid2">
            <NumberField label={t('Centre X')} limit={POS_LIMIT} value={m.hoopCenter.x} onChange={(v) => setM({ hoopCenter: { ...m.hoopCenter!, x: v } }, 'hc')} />
            <NumberField label={t('Centre Y')} limit={POS_LIMIT} value={m.hoopCenter.y} onChange={(v) => setM({ hoopCenter: { ...m.hoopCenter!, y: v } }, 'hc')} />
          </div>
        )}
      </div>
      <div className="section">
        <h3>{t('Machine')}</h3>
        <div className="grid2">
          <NumberField label={t(L.trimThreshold.label)} limit={L.trimThreshold} value={m.trimThreshold} onChange={(v) => setM({ trimThreshold: v }, 'trim')} />
          <NumberField label={t(L.connectStitchMax.label)} limit={L.connectStitchMax} value={m.connectStitchMax} onChange={(v) => setM({ connectStitchMax: v }, 'conn')} />
          <NumberField label={t(L.maxStitchLength.label)} limit={L.maxStitchLength} value={m.maxStitchLength} onChange={(v) => setM({ maxStitchLength: v }, 'maxst')} />
          <NumberField label={t(L.speedSpm.label)} limit={L.speedSpm} decimals={0} value={m.speedSpm} onChange={(v) => setM({ speedSpm: v }, 'spm')} />
          <NumberField label={t(L.colorChangeSeconds.label)} limit={L.colorChangeSeconds} decimals={0} value={m.colorChangeSeconds} onChange={(v) => setM({ colorChangeSeconds: v }, 'ccs')} />
          <NumberField label={t(L.trimSeconds.label)} limit={L.trimSeconds} decimals={1} value={m.trimSeconds} onChange={(v) => setM({ trimSeconds: v }, 'ts')} />
        </div>
      </div>
      {stats && (
        <div className="section">
          <h3>{t('Statistics')}</h3>
          <dl className="kv">
            <dt>{t('Stitches')}</dt>
            <dd>{fmtInt(stats.stitches)}</dd>
            <dt>{t('Size')}</dt>
            <dd>
              {stats.widthMm.toFixed(1)} × {stats.heightMm.toFixed(1)} mm
            </dd>
            <dt>{t('Colours')}</dt>
            <dd>{stats.uniqueColors}</dd>
            <dt>{t('Colour changes')}</dt>
            <dd>{stats.colorChanges}</dd>
            <dt>{t('Jumps')}</dt>
            <dd>{stats.jumps}</dd>
            <dt>{t('Trims')}</dt>
            <dd>{stats.trims}</dd>
            <dt>{t('Thread (top thread)')}</dt>
            <dd>{stats.threadLengthM.toFixed(1)} m</dd>
            <dt>{t('Sewing time (estimate)')}</dt>
            <dd>≈ {formatDuration(stats.estimatedSeconds)}</dd>
          </dl>
          <p className="hint">{t('The sewing time is a rough estimate based on the speed, colour changes and trims above. The actual time depends on the machine.')}</p>
        </div>
      )}
    </>
  );
}

function scaleAxis(d: Design, sx: number, sy: number): Design {
  return {
    ...d,
    objects: d.objects.map((o) => ({
      ...o,
      transform: { ...o.transform, x: o.transform.x * sx, y: o.transform.y * sy, scaleX: o.transform.scaleX * sx, scaleY: o.transform.scaleY * sy },
      entry: o.entry ? { x: o.entry.x * sx, y: o.entry.y * sy } : null,
      exit: o.exit ? { x: o.exit.x * sx, y: o.exit.y * sy } : null,
    })),
  };
}
